import { toFiniteNumber } from "../blueprint/runtime/helpers.js";
import type { LocationValue, TransformValue } from "../blueprint/types.js";
import { resolvePath } from "../layout/resolution.js";
import type {
  BarLoaderRuntimeTarget,
  RenderBarLoaderNode,
  RenderBarLoaderProps,
  RenderBarLoaderTargetPatch,
  RenderContainerNode,
  RenderCustomDisciplineButtonNode,
  RenderCustomDisciplineInputNode,
  RenderImageNode,
  RenderIconNode,
  RenderLayoutListNode,
  RenderNodeBase,
  RenderQrCodeNode,
  RenderShapeNode,
  RenderTextNode,
  RenderVideoNode,
  RuntimePatch,
} from "./types.js";

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

function parseLocationPatch(
  value: unknown,
): Partial<LocationValue> | undefined {
  if (!isPlainRecord(value)) {
    return undefined;
  }

  const result: Partial<LocationValue> = {};

  if (Object.prototype.hasOwnProperty.call(value, "x")) {
    result.x = toFiniteNumber(value.x);
  }
  if (Object.prototype.hasOwnProperty.call(value, "y")) {
    result.y = toFiniteNumber(value.y);
  }
  if (Object.prototype.hasOwnProperty.call(value, "z")) {
    result.z = toFiniteNumber(value.z);
  }

  return Object.keys(result).length > 0 ? result : undefined;
}

function parseTransformPatch(
  value: unknown,
): Partial<TransformValue> | undefined {
  if (!isPlainRecord(value)) {
    return undefined;
  }

  const result: Partial<TransformValue> = {};

  if (Object.prototype.hasOwnProperty.call(value, "width")) {
    result.width = toFiniteNumber(value.width);
  }
  if (Object.prototype.hasOwnProperty.call(value, "height")) {
    result.height = toFiniteNumber(value.height);
  }
  if (Object.prototype.hasOwnProperty.call(value, "rotation")) {
    result.rotation = toFiniteNumber(value.rotation);
  }

  return Object.keys(result).length > 0 ? result : undefined;
}

type BorderCompositeValue = {
  border?: { top: string; right: string; bottom: string; left: string };
};

type BorderRadiusCompositeValue = {
  radius?: {
    topLeft: string;
    topRight: string;
    bottomRight: string;
    bottomLeft: string;
  };
};

type SpacingSideBlock = {
  value?: number;
  sides?: { top: number; right: number; bottom: number; left: number };
};

type SpacingCompositeValue = {
  margin?: SpacingSideBlock;
  padding?: SpacingSideBlock;
};

function resolveSpacingSides(
  block: SpacingSideBlock | undefined,
): { top: number; right: number; bottom: number; left: number } | undefined {
  if (!block) {
    return undefined;
  }

  if (block.sides) {
    return block.sides;
  }

  const value = Number(block.value);
  if (!Number.isFinite(value)) {
    return undefined;
  }

  return { top: value, right: value, bottom: value, left: value };
}

function normalizeCustomDisciplineInputOptions(
  value: unknown,
  valuePath = "id",
  labelPath = "name",
): RenderCustomDisciplineInputNode["options"] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.map((item) => {
    if (isPlainRecord(item)) {
      const rawValue =
        item.value ?? resolvePath(item, valuePath) ?? resolvePath(item, "id");
      const rawLabel =
        item.label ??
        resolvePath(item, labelPath) ??
        resolvePath(item, "name") ??
        rawValue;
      return {
        value: String(rawValue ?? ""),
        label: String(rawLabel ?? ""),
      };
    }
    return {
      value: String(item ?? ""),
      label: String(item ?? ""),
    };
  });
}

/**
 * Expands composite `border`, `borderRadius` and `padding` patch values onto flat
 * CSS style keys. Mirrors {@link applyBarLoaderTargetPatch} without per-side guards.
 *
 * @returns True when the patch key was handled as a composite style value.
 */
function applyCompositeStylePatch(
  style: Record<string, unknown>,
  key: string,
  value: unknown,
): boolean {
  if (key === "border" && value && typeof value === "object") {
    const borderValue = value as BorderCompositeValue;
    if (borderValue.border) {
      const { top, right, bottom, left } = borderValue.border;
      style.borderTop = top;
      style.borderRight = right;
      style.borderBottom = bottom;
      style.borderLeft = left;
      style.border =
        top === right && right === bottom && bottom === left ? top : undefined;
    }
    return true;
  }

  if (key === "borderRadius" && value && typeof value === "object") {
    const radiusValue = value as BorderRadiusCompositeValue;
    if (radiusValue.radius) {
      const { topLeft, topRight, bottomRight, bottomLeft } = radiusValue.radius;
      style.borderTopLeftRadius = topLeft;
      style.borderTopRightRadius = topRight;
      style.borderBottomRightRadius = bottomRight;
      style.borderBottomLeftRadius = bottomLeft;
      style.borderRadius =
        topLeft === topRight &&
        topRight === bottomRight &&
        bottomRight === bottomLeft
          ? topLeft
          : undefined;
    }
    return true;
  }

  if (key === "padding" && value && typeof value === "object") {
    const spacingValue = value as SpacingCompositeValue;
    const marginSides = resolveSpacingSides(spacingValue.margin);
    const paddingSides = resolveSpacingSides(spacingValue.padding);

    if (marginSides) {
      style.marginTop = `${marginSides.top}px`;
      style.marginRight = `${marginSides.right}px`;
      style.marginBottom = `${marginSides.bottom}px`;
      style.marginLeft = `${marginSides.left}px`;
      const allMarginSame =
        marginSides.top === marginSides.right &&
        marginSides.right === marginSides.bottom &&
        marginSides.bottom === marginSides.left;
      style.margin = allMarginSame ? `${marginSides.top}px` : undefined;
    }

    if (paddingSides) {
      style.paddingTop = `${paddingSides.top}px`;
      style.paddingRight = `${paddingSides.right}px`;
      style.paddingBottom = `${paddingSides.bottom}px`;
      style.paddingLeft = `${paddingSides.left}px`;
      const allPaddingSame =
        paddingSides.top === paddingSides.right &&
        paddingSides.right === paddingSides.bottom &&
        paddingSides.bottom === paddingSides.left;
      style.padding = allPaddingSame ? `${paddingSides.top}px` : undefined;
    }

    return true;
  }

  return false;
}

function applySimpleStylePatch(
  style: Record<string, unknown>,
  key: string,
  value: unknown,
): void {
  if (applyCompositeStylePatch(style, key, value)) {
    return;
  }

  style[key] = value;
}

/**
 * Applies `set(location)` / `set(transform)` patches onto layout geometry fields.
 */
function applyGeometryPatch<T extends RenderNodeBase>(
  renderNode: T,
  patch: RuntimePatch,
): T {
  if (patch.key === "location") {
    const location = parseLocationPatch(patch.value);
    if (!location) {
      return renderNode;
    }

    return {
      ...renderNode,
      ...(location.x != null ? { x: location.x } : {}),
      ...(location.y != null ? { y: location.y } : {}),
      ...(location.z != null ? { zIndex: Math.trunc(location.z) } : {}),
    };
  }

  if (patch.key === "transform") {
    const transform = parseTransformPatch(patch.value);
    if (!transform) {
      return renderNode;
    }

    return {
      ...renderNode,
      ...(transform.width != null ? { width: transform.width } : {}),
      ...(transform.height != null ? { height: transform.height } : {}),
      ...(transform.rotation != null ? { rotation: transform.rotation } : {}),
    };
  }

  return renderNode;
}

/**
 * Applies a behavior flow patch onto a compiled text render node.
 */
export function applyPatchToTextNode(
  renderNode: RenderTextNode,
  patch: RuntimePatch,
): RenderTextNode {
  if (patch.nodeId !== renderNode.id) {
    return renderNode;
  }

  if (patch.key === "location" || patch.key === "transform") {
    return applyGeometryPatch(
      { ...renderNode, style: { ...renderNode.style } },
      patch,
    );
  }

  const nextNode: RenderTextNode = {
    ...renderNode,
    style: { ...renderNode.style },
  };

  switch (patch.key) {
    case "setBinding":
      nextNode.text = String(patch.value ?? "");
      break;

    case "color":
      nextNode.style.color = patch.value;
      break;

    case "backgroundColor":
      nextNode.style.backgroundColor = patch.value;
      break;

    case "visibility":
      nextNode.style.visibility = patch.value;
      break;

    default:
      applySimpleStylePatch(nextNode.style, patch.key, patch.value);
      break;
  }

  return nextNode;
}

/**
 * Applies a behavior flow patch onto a compiled container render node.
 */
export function applyPatchToContainerNode(
  renderNode: RenderContainerNode,
  patch: RuntimePatch,
): RenderContainerNode {
  if (patch.nodeId !== renderNode.id) {
    return renderNode;
  }

  if (patch.key === "location" || patch.key === "transform") {
    return applyGeometryPatch(
      { ...renderNode, style: { ...renderNode.style } },
      patch,
    );
  }

  const nextNode: RenderContainerNode = {
    ...renderNode,
    style: { ...renderNode.style },
  };

  switch (patch.key) {
    case "visibility":
      nextNode.style.visibility = patch.value;
      break;

    case "backgroundColor":
      nextNode.style.backgroundColor = patch.value;
      break;

    case "color":
      nextNode.style.color = patch.value;
      break;

    default:
      applySimpleStylePatch(nextNode.style, patch.key, patch.value);
      break;
  }

  return nextNode;
}

/**
 * Applies a behavior flow patch onto a compiled shape render node.
 */
export function applyPatchToShapeNode(
  renderNode: RenderShapeNode,
  patch: RuntimePatch,
): RenderShapeNode {
  if (patch.nodeId !== renderNode.id) {
    return renderNode;
  }

  if (patch.key === "location" || patch.key === "transform") {
    return applyGeometryPatch(
      { ...renderNode, style: { ...renderNode.style } },
      patch,
    );
  }

  const nextNode: RenderShapeNode = {
    ...renderNode,
    style: { ...renderNode.style },
  };

  switch (patch.key) {
    case "visibility":
      nextNode.style.visibility = patch.value;
      break;

    case "backgroundColor":
      nextNode.style.backgroundColor = patch.value;
      break;

    case "color":
      nextNode.style.color = patch.value;
      break;

    default:
      applySimpleStylePatch(nextNode.style, patch.key, patch.value);
      break;
  }

  return nextNode;
}

/**
 * Applies a behavior flow patch onto a compiled image render node.
 */
export function applyPatchToImageNode(
  renderNode: RenderImageNode,
  patch: RuntimePatch,
): RenderImageNode {
  if (patch.nodeId !== renderNode.id) {
    return renderNode;
  }

  if (patch.key === "location" || patch.key === "transform") {
    return applyGeometryPatch(
      { ...renderNode, style: { ...renderNode.style } },
      patch,
    );
  }

  const nextNode: RenderImageNode = {
    ...renderNode,
    style: { ...renderNode.style },
  };

  switch (patch.key) {
    case "setBinding":
      nextNode.src = String(patch.value ?? "");
      break;

    case "visibility":
      nextNode.style.visibility = patch.value;
      break;

    case "backgroundColor":
      nextNode.style.backgroundColor = patch.value;
      break;

    case "color":
      nextNode.style.color = patch.value;
      break;

    default:
      applySimpleStylePatch(nextNode.style, patch.key, patch.value);
      break;
  }

  return nextNode;
}

/**
 * Applies a behavior flow patch onto a compiled video render node.
 */
export function applyPatchToVideoNode(
  renderNode: RenderVideoNode,
  patch: RuntimePatch,
): RenderVideoNode {
  if (patch.nodeId !== renderNode.id) {
    return renderNode;
  }

  if (patch.key === "location" || patch.key === "transform") {
    return applyGeometryPatch(
      { ...renderNode, style: { ...renderNode.style } },
      patch,
    );
  }

  const nextNode: RenderVideoNode = {
    ...renderNode,
    style: { ...renderNode.style },
  };

  switch (patch.key) {
    case "setBinding":
      nextNode.src = String(patch.value ?? "");
      break;

    case "muted":
      nextNode.muted = Boolean(patch.value);
      break;

    case "loop":
      nextNode.loop = Boolean(patch.value);
      break;

    case "visibility":
      nextNode.style.visibility = patch.value;
      break;

    case "backgroundColor":
      nextNode.style.backgroundColor = patch.value;
      break;

    case "color":
      nextNode.style.color = patch.value;
      break;

    default:
      applySimpleStylePatch(nextNode.style, patch.key, patch.value);
      break;
  }

  return nextNode;
}

const BAR_LOADER_PROP_PATCH_KEYS = new Set(["totalWeight", "barWeight"]);

function barLoaderTargetPatchKey(
  target: BarLoaderRuntimeTarget,
  key: string,
): string {
  return `${target.weight}|${target.part}|${key}`;
}

function mergeBarLoaderTargetPatch(
  patches: RenderBarLoaderTargetPatch[],
  target: BarLoaderRuntimeTarget,
  key: string,
  value: unknown,
): RenderBarLoaderTargetPatch[] {
  const nextKey = barLoaderTargetPatchKey(target, key);
  const byKey = new Map(
    patches.map((patch) => [
      barLoaderTargetPatchKey(patch.target, patch.key),
      patch,
    ]),
  );
  byKey.set(nextKey, { target, key, value });
  return Array.from(byKey.values());
}

/**
 * Applies a behavior flow patch onto a compiled bar-loader render node.
 */
export function applyPatchToBarLoaderNode(
  renderNode: RenderBarLoaderNode,
  patch: RuntimePatch,
): RenderBarLoaderNode {
  if (patch.nodeId !== renderNode.id) {
    return renderNode;
  }

  if (patch.key === "location" || patch.key === "transform") {
    return applyGeometryPatch(
      { ...renderNode, style: { ...renderNode.style } },
      patch,
    );
  }

  if (patch.barLoaderTarget) {
    return {
      ...renderNode,
      style: { ...renderNode.style },
      props: { ...renderNode.props },
      barLoaderTargetPatches: mergeBarLoaderTargetPatch(
        renderNode.barLoaderTargetPatches ?? [],
        patch.barLoaderTarget,
        patch.key,
        patch.value,
      ),
    };
  }

  if (patch.barLoaderTargetScope) {
    return renderNode;
  }

  if (BAR_LOADER_PROP_PATCH_KEYS.has(patch.key)) {
    const nextProps: RenderBarLoaderProps = {
      ...renderNode.props,
      [patch.key]: patch.value,
    };
    return {
      ...renderNode,
      style: { ...renderNode.style },
      props: nextProps,
      barLoaderTargetPatches: renderNode.barLoaderTargetPatches,
    };
  }

  const nextNode: RenderBarLoaderNode = {
    ...renderNode,
    style: { ...renderNode.style },
    props: { ...renderNode.props },
    barLoaderTargetPatches: renderNode.barLoaderTargetPatches,
  };

  switch (patch.key) {
    case "setBinding":
      nextNode.props.totalWeight = Number(patch.value);
      break;
    case "barWeight":
      nextNode.props.barWeight = Number(patch.value);
      break;
    case "totalWeight":
      nextNode.props.totalWeight = Number(patch.value);
      break;
    case "visibility":
      nextNode.style.visibility = patch.value;
      break;

    case "backgroundColor":
      nextNode.style.backgroundColor = patch.value;
      break;

    case "color":
      nextNode.style.color = patch.value;
      break;

    default:
      applySimpleStylePatch(nextNode.style, patch.key, patch.value);
      break;
  }

  return nextNode;
}

/**
 * Applies a behavior flow patch onto a compiled layout list render node.
 */
export function applyPatchToLayoutListNode(
  renderNode: RenderLayoutListNode,
  patch: RuntimePatch,
): RenderLayoutListNode {
  if (patch.nodeId !== renderNode.id) {
    return renderNode;
  }

  if (patch.key === "location" || patch.key === "transform") {
    return applyGeometryPatch(
      { ...renderNode, style: { ...renderNode.style } },
      patch,
    );
  }

  const nextNode: RenderLayoutListNode = {
    ...renderNode,
    style: { ...renderNode.style },
  };

  switch (patch.key) {
    case "visibility":
      nextNode.style.visibility = patch.value;
      break;

    case "backgroundColor":
      nextNode.style.backgroundColor = patch.value;
      break;

    case "color":
      nextNode.style.color = patch.value;
      break;

    default:
      applySimpleStylePatch(nextNode.style, patch.key, patch.value);
      break;
  }

  return nextNode;
}

/**
 * Applies a behavior flow patch onto a compiled QR-code render node.
 */
export function applyPatchToQrCodeNode(
  renderNode: RenderQrCodeNode,
  patch: RuntimePatch,
): RenderQrCodeNode {
  if (patch.nodeId !== renderNode.id) {
    return renderNode;
  }

  if (patch.key === "location" || patch.key === "transform") {
    return applyGeometryPatch(
      { ...renderNode, style: { ...renderNode.style } },
      patch,
    );
  }

  const nextNode: RenderQrCodeNode = {
    ...renderNode,
    style: { ...renderNode.style },
  };

  switch (patch.key) {
    case "setBinding":
      nextNode.value = String(patch.value ?? "");
      break;

    case "visibility":
      nextNode.style.visibility = patch.value;
      break;

    case "backgroundColor":
      nextNode.style.backgroundColor = patch.value;
      break;

    case "color":
      nextNode.style.color = patch.value;
      break;

    default:
      applySimpleStylePatch(nextNode.style, patch.key, patch.value);
      break;
  }

  return nextNode;
}

/**
 * Applies a behavior flow patch onto a compiled MUI icon render node.
 */
export function applyPatchToIconNode(
  renderNode: RenderIconNode,
  patch: RuntimePatch,
): RenderIconNode {
  if (patch.nodeId !== renderNode.id) {
    return renderNode;
  }

  if (patch.key === "location" || patch.key === "transform") {
    return applyGeometryPatch(
      { ...renderNode, style: { ...renderNode.style } },
      patch,
    );
  }

  const nextNode: RenderIconNode = {
    ...renderNode,
    style: { ...renderNode.style },
    props: { ...renderNode.props },
  };

  switch (patch.key) {
    case "setBinding":
    case "iconName":
      nextNode.props.iconName =
        patch.value == null ? null : String(patch.value);
      break;

    case "iconSize":
    case "size":
    case "fontSize": {
      const nextSize = toFiniteNumber(patch.value);
      if (nextSize != null) {
        nextNode.props.size = nextSize;
      }
      break;
    }

    case "visibility":
      nextNode.style.visibility = patch.value;
      break;

    case "backgroundColor":
      nextNode.style.backgroundColor = patch.value;
      break;

    case "iconColor":
    case "color":
      nextNode.props.color = patch.value == null ? null : String(patch.value);
      nextNode.style.color = patch.value;
      break;

    default:
      applySimpleStylePatch(nextNode.style, patch.key, patch.value);
      break;
  }

  return nextNode;
}

/**
 * Applies a behavior flow patch onto a compiled custom-discipline button
 * render node. Multi-input actions (`customListSetField`, `customOpenModal`, …)
 * also land here as sticky patches, but only their `customDiscipline.action`
 * runtime effect (see `compileFlow.ts`) is meaningful to the host page.
 */
export function applyPatchToCustomDisciplineButtonNode(
  renderNode: RenderCustomDisciplineButtonNode,
  patch: RuntimePatch,
): RenderCustomDisciplineButtonNode {
  if (patch.nodeId !== renderNode.id) {
    return renderNode;
  }

  if (patch.key === "location" || patch.key === "transform") {
    return applyGeometryPatch(
      { ...renderNode, style: { ...renderNode.style } },
      patch,
    );
  }

  const nextNode: RenderCustomDisciplineButtonNode = {
    ...renderNode,
    style: { ...renderNode.style },
    props: { ...renderNode.props },
  };

  switch (patch.key) {
    case "customActionTooltip":
      nextNode.tooltip = patch.value
        ? { kind: "text", text: String(patch.value) }
        : undefined;
      break;
    case "customActionLabel":
      nextNode.props.label = String(patch.value ?? "");
      break;
    case "customActionDisabled":
      nextNode.props.disabled = Boolean(patch.value);
      break;
    case "customActionIcon":
    case "customActionStartIcon":
      nextNode.props.startIcon =
        patch.value == null ? undefined : String(patch.value);
      break;
    case "customActionVariant":
    case "customActionSize":
    case "customActionColor": {
      const property =
        patch.key === "customActionVariant"
          ? "variant"
          : patch.key === "customActionSize"
            ? "size"
            : "color";
      Object.assign(nextNode.props, { [property]: patch.value });
      break;
    }
    case "visibility":
      nextNode.style.visibility = patch.value;
      break;

    case "backgroundColor":
      nextNode.style.backgroundColor = patch.value;
      break;

    case "color":
      nextNode.style.color = patch.value;
      break;

    default:
      applySimpleStylePatch(nextNode.style, patch.key, patch.value);
      break;
  }

  return nextNode;
}

/**
 * Applies a behavior flow patch onto a compiled custom-discipline input
 * render node. The controlled `value` itself is not patchable (it flows
 * from `data.inputValues`); behavior may override the option list and style.
 */
export function applyPatchToCustomDisciplineInputNode(
  renderNode: RenderCustomDisciplineInputNode,
  patch: RuntimePatch,
): RenderCustomDisciplineInputNode {
  if (patch.nodeId !== renderNode.id) {
    return renderNode;
  }

  if (patch.key === "location" || patch.key === "transform") {
    return applyGeometryPatch(
      { ...renderNode, style: { ...renderNode.style } },
      patch,
    );
  }

  const nextNode: RenderCustomDisciplineInputNode = {
    ...renderNode,
    style: { ...renderNode.style },
  };

  switch (patch.key) {
    case "visibility":
      nextNode.style.visibility = patch.value;
      break;

    case "backgroundColor":
      nextNode.style.backgroundColor = patch.value;
      break;

    case "color":
      nextNode.style.color = patch.value;
      break;

    case "customInputOptions":
      nextNode.options = normalizeCustomDisciplineInputOptions(
        patch.value,
        nextNode.props.optionValuePath ?? "id",
        nextNode.props.optionLabelPath ?? "name",
      );
      break;

    default:
      applySimpleStylePatch(nextNode.style, patch.key, patch.value);
      break;
  }

  return nextNode;
}
